import { lazy, Suspense, type ReactNode } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import type { MeResponse } from '@ucp/web/lib';
import { myWorkAppliesToMe } from '../../lib/myWork/applies';
import { HomeView } from '../home/HomeView';
import { RedirectSplat } from './RedirectSplat';
import { RouteGuard } from './RouteGuard';
import { RouteLoading } from './RouteLoading';
import { ModulePlaceholder } from './ModulePlaceholder';
import { modulesConfig, nestedPlaceholderPaths, PLACEHOLDER_PLANNING_DISCLAIMER } from './modulesConfig';
import { placeholderWikiHref } from './placeholderWiki';
import { NotFoundView } from './NotFoundView';
import { WelcomeView } from './WelcomeView';
import {
  APPROVALS_ROLES,
  COMPLAINTS_ROUTE_ROLES,
  COMPOSE_ROLES,
  PLATFORM_ADMIN_ROLES,
  SCHOOL_TEMPLATE_ROLES,
} from './routeRoles';
// Route guards need these role arrays synchronously, so they are imported from the
// dependency-light `*Roles` modules rather than the (lazy-loaded) surface files —
// importing them from the surfaces would drag the surface back into the initial
// chunk and defeat the split.
import { ATR_ROLES } from '../hr/atrRoles';
import { HR_PEOPLE_ASSISTANT_ROLES } from '../human-resources/peopleAssistantRoles';
import { HR_INTERVIEW_COMPLIANCE_ROLES } from '../human-resources/interviewComplianceRoles';
import { HR_ER_MEETING_ROLES } from '../human-resources/erMeetingRoles';
import { HR_CASE_ROLES } from '../human-resources/hrCaseRoles';
import { POLICY_ROLES } from '../policies/policyRoles';
import { INSIGHTS_ROUTE_ROLES } from '../insights/insightsRoles';
import { REFERRAL_ROLES } from '../referrals/referralRoles';
import { COMMERCIAL_SITES_ROLES, COMMERCIAL_DATA_ROLES } from '../commercial/commercialRoles';
import { PURCHASING_BUYER_ROLES, PURCHASING_RECONCILE_ROLES } from '../finance/purchasingRoles';
import { SERVICE_OPS_FINANCE_ROLES, SERVICE_OPS_PEOPLE_ROLES } from '../service-ops/serviceOpsRoles';
import { ATTENDANCE_ROUTE_ROLES } from '../attendance/attendanceRoles';
import { tripsRouteRoles } from '../trips/tripsRoles';
import { OPS_ROUTE_ROLES } from '../ops/opsRoles';
import { OFSTED_ROUTE_ROLES, ofstedModuleEnabled } from '../ofsted/ofstedRoles';
import { SURVEY_ROUTE_ROLES } from '../surveys/surveyRoles';
import { MARKETING_ROUTE_ROLES } from '../marketing/marketingRoles';
import { PUPIL_RISK_ROUTE_ROLES } from '../pupil-risk/pupilRiskRoles';

// Route-level code-splitting: each module surface is lazy-loaded so its code (and
// transitive deps) lands in a per-module chunk fetched on demand, instead of the
// single ~1 MB initial bundle. Small, always-needed pieces (HomeView, RouteGuard,
// redirects, placeholders, NotFound) stay eager. Recharts is already split at the
// chart level (ChartWithTable). All surfaces are named exports, hence the `.then`
// default shim required by `React.lazy`.
const ComposeRoute = lazy(() =>
  import('./ComposeRoute').then((m) => ({ default: m.ComposeRoute })),
);
const ApprovalSurfaces = lazy(() =>
  import('../approvals/ApprovalSurfaces').then((m) => ({ default: m.ApprovalSurfaces })),
);
const PlatformRoutes = lazy(() =>
  import('../platform/PlatformRoutes').then((m) => ({ default: m.PlatformRoutes })),
);
const SettingsRoutes = lazy(() =>
  import('../settings/SettingsRoutes').then((m) => ({ default: m.SettingsRoutes })),
);
const SchoolTemplatesPage = lazy(() =>
  import('../templates/SchoolTemplatesPage').then((m) => ({ default: m.SchoolTemplatesPage })),
);
const CrossSchoolOpsView = lazy(() =>
  import('../CrossSchoolOpsView').then((m) => ({ default: m.CrossSchoolOpsView })),
);
const ScheduledSendsView = lazy(() =>
  import('../schedule/ScheduledSendsView').then((m) => ({ default: m.ScheduledSendsView })),
);
const PanelMemberSurface = lazy(() =>
  import('../complaints/panel/PanelMemberSurface').then((m) => ({ default: m.PanelMemberSurface })),
);
const ComplaintsSurface = lazy(() =>
  import('../complaints/ComplaintsSurface').then((m) => ({ default: m.ComplaintsSurface })),
);
const AtrSurface = lazy(() => import('../hr/AtrSurface').then((m) => ({ default: m.AtrSurface })));
const HrPeopleAssistantSurface = lazy(() =>
  import('../human-resources/HrPeopleAssistantSurface').then((m) => ({
    default: m.HrPeopleAssistantSurface,
  })),
);
const InterviewComplianceSurface = lazy(() =>
  import('../human-resources/InterviewComplianceSurface').then((m) => ({
    default: m.InterviewComplianceSurface,
  })),
);
const PoliciesSurface = lazy(() =>
  import('../policies/PoliciesSurface').then((m) => ({ default: m.PoliciesSurface })),
);
const ServiceOpsSurface = lazy(() =>
  import('../service-ops/ServiceOpsSurface').then((m) => ({ default: m.ServiceOpsSurface })),
);
const HrCasesSurface = lazy(() =>
  import('../human-resources/cases/HrCasesSurface').then((m) => ({ default: m.HrCasesSurface })),
);
const InsightsSurface = lazy(() =>
  import('../insights/InsightsSurface').then((m) => ({ default: m.InsightsSurface })),
);
const AttendanceSurface = lazy(() =>
  import('../attendance/AttendanceSurface').then((m) => ({ default: m.AttendanceSurface })),
);
const TripsSurface = lazy(() =>
  import('../trips/TripsSurface').then((m) => ({ default: m.TripsSurface })),
);
const WeeklyReportSurface = lazy(() =>
  import('../ops/WeeklyReportSurface').then((m) => ({ default: m.WeeklyReportSurface })),
);
const OfstedSurface = lazy(() =>
  import('../ofsted/OfstedSurface').then((m) => ({ default: m.OfstedSurface })),
);
const MarketingSurface = lazy(() =>
  import('../marketing/MarketingSurface').then((m) => ({ default: m.MarketingSurface })),
);
const PupilRiskSurface = lazy(() =>
  import('../pupil-risk/PupilRiskSurface').then((m) => ({ default: m.PupilRiskSurface })),
);
const MyWorkPage = lazy(() =>
  import('../my-work/MyWorkPage').then((m) => ({ default: m.MyWorkPage })),
);
const SurveysRoutes = lazy(() =>
  import('../surveys/SurveysRoutes').then((m) => ({ default: m.SurveysRoutes })),
);
const ReferralsSurface = lazy(() =>
  import('../referrals/ReferralsSurface').then((m) => ({ default: m.ReferralsSurface })),
);
const ProfileSettingsPage = lazy(() =>
  import('../account/ProfileSettingsPage').then((m) => ({ default: m.ProfileSettingsPage })),
);
const CommercialSitesProjectionSurface = lazy(() =>
  import('../commercial/CommercialSitesProjectionSurface').then((m) => ({
    default: m.CommercialSitesProjectionSurface,
  })),
);
const CommercialRoutes = lazy(() =>
  import('../commercial/CommercialRoutes').then((m) => ({ default: m.CommercialRoutes })),
);
const PurchasingSurface = lazy(() =>
  import('../finance/purchasing/PurchasingSurface').then((m) => ({ default: m.PurchasingSurface })),
);
const ReconciliationSurface = lazy(() =>
  import('../finance/purchasing/ReconciliationSurface').then((m) => ({
    default: m.ReconciliationSurface,
  })),
);

/**
 * The single route table (PRO-0250 / ADR-014, + Amendment 1 PRO-0340, + A3 nested
 * deep modules). Shared by the live shell (`RoutedAppShell`, under `BrowserRouter`)
 * and the integration tests (under `MemoryRouter`) so the route→surface→guard mapping
 * has one definition and cannot drift.
 *
 * Nested HR/Finance modules: module dashboard at `/hr` / `/finance`; sub-module task
 * routes driven from `modulesConfig.subModules[].tasks`. All placeholders are
 * intentionally **ungated** (D4) and inert. Any *functional* route MUST be
 * capability-gated in its own future PRD (SEC-4 forward control).
 */
function HrAssistantRouteGuard({ user, children }: { user: MeResponse; children: ReactNode }) {
  const loc = useLocation();
  const path = loc.pathname.replace(/\/+$/, '') || '/';
  const isKnowledge = path === '/hr/people-assistant/knowledge';
  const isIndex = path === '/hr/people-assistant';
  const allowL6 = isKnowledge || isIndex;
  return (
    <RouteGuard
      user={user}
      anyOf={
        allowL6
          ? [...HR_PEOPLE_ASSISTANT_ROLES, 'L6_PLATFORM_ADMIN']
          : [...HR_PEOPLE_ASSISTANT_ROLES]
      }
      {...(isKnowledge ? {} : { baselineCapability: 'hr_assistant_ask' as const })}
      label={isKnowledge || isIndex ? 'Ask TAG knowledge' : 'Ask TAG'}
    >
      {children}
    </RouteGuard>
  );
}

export function AppRoutes({ user }: { user: MeResponse }) {
  // PRO-0380 / ADR-018: the Complaints module promotes from its coming-soon
  // nested placeholder to functional routes ONLY when the flag is on; the server
  // `requireComplaintsEnabled` gate remains authoritative (SF-005).
  const complaintsEnabled = user.featureFlags.complaintsEnabled;
  // PRO-0490 / ADR-024: the school-scoped template-authoring surface mounts only
  // when the flag is on; the server `requireSchoolTemplateAuthoringEnabled` gate
  // stays authoritative. It is a static sibling of the L6-only `/settings/*`
  // splat, so react-router ranks it higher and it keeps its OWN
  // L2_APPROVER/PRINCIPAL/OPS_DIRECTOR guard (2026-07-13 amendment adds L2; the L6
  // settings guard is never widened).
  const schoolTemplateAuthoringEnabled = user.featureFlags.schoolTemplateAuthoringEnabled;
  // PRO-0530 / ADR-018/029: the ATR sub-module of the (still coming-soon) HR module
  // promotes from its inert placeholder to the functional, capability-gated
  // `AtrSurface` ONLY when the flag is on; the server `requireAtrEnabled` gate stays
  // authoritative (a stale-true client still 503s in EP2). Only the ATR sub-module
  // promotes — the other HR sub-modules and the `/hr` dashboard stay placeholders
  // (mirrors how a sub-feature is gated within a module).
  const atrEnabled = user.featureFlags.atrEnabled;
  const atrBasePath = '/hr/request-new-hire-atr';
  // PRO-3190. CEO is in ATR_ROLES, but a CEO-only caller enters only while All
  // requests is on. Flag off, the guard is the list without CEO, so the existing
  // refusal renders and AtrSurface does not mount. A CEO who also holds another
  // ATR role still matches that role.
  const atrRouteRoles =
    user.featureFlags.atrEstateRegisterEnabled === true
      ? ATR_ROLES
      : ATR_ROLES.filter((role) => role !== 'CEO');
  // PRO-1100 / ADR-056/057/058: the People Assistant sub-module of the (still
  // coming-soon) HR module promotes from its inert placeholder to the functional,
  // capability-gated `HrPeopleAssistantSurface` ONLY when `hrAssistantEnabled` is on;
  // the server `requireHrAssistantEnabled` (+ `requireHrAssistantAiEnabled` on the ask
  // path) + `hr_assistant_ask` / `hr_knowledge_manage` gates stay authoritative (a
  // stale-true client still 503s/403s). The RouteGuard is defence-in-depth (never L6 —
  // EoP). Only this sub-module promotes — the other HR sub-modules stay placeholders.
  const hrAssistantEnabled = user.featureFlags.hrAssistantEnabled ?? false;
  const hrAssistantBasePath = '/hr/people-assistant';
  // PRO-1120 / ADR-056 A1 / ADR-059/060 / ADR-025 A7: the Interview Compliance
  // sub-module of the (still coming-soon) HR module promotes from its inert placeholder
  // to the functional, capability-gated `InterviewComplianceSurface` ONLY when
  // `hrInterviewComplianceEnabled` is on; the server `requireHrInterviewComplianceEnabled`
  // (+ `requireHrInterviewComplianceAiEnabled` on the analyse path,
  // `requireHrInterviewTeamsIngestEnabled` on the Teams-pull path) + the
  // `interview_compliance_run` / `interview_checklist_manage` /
  // `interview_compliance_oversight` gates stay authoritative (a stale-true client still
  // 503s/403s). The RouteGuard is defence-in-depth (never L6 — EoP). Only this
  // sub-module promotes — the other HR sub-modules stay placeholders.
  const hrInterviewComplianceEnabled = user.featureFlags.hrInterviewComplianceEnabled ?? false;
  const hrInterviewComplianceBasePath = '/hr/interview-compliance';
  // PRO-1290 / ADR-068: Employee Relations shares the Interview Compliance surface (a nav
  // re-parent, ADR-014) but NOT its flag — enabling recruitment interview analysis must never
  // enable disciplinary-hearing records, and vice versa. So the shared surface mounts on
  // EITHER flag and decides internally which tabs exist; the server
  // (`requireHrErMeetingsEnabled` 503-before-403 + `er_meeting_compliance_run` /
  // `er_checklist_manage` + the case-confidential RLS) stays authoritative.
  const hrErMeetingsEnabled = user.featureFlags.hrErMeetingsEnabled ?? false;
  const interviewComplianceSurfaceMounted = hrInterviewComplianceEnabled || hrErMeetingsEnabled;
  // PRO-1200 / ADR-062: the Policies sub-module of the (still coming-soon) HR module
  // promotes from its inert placeholder to the functional, capability-gated
  // `PoliciesSurface` ONLY when `policyManagementEnabled` is on (default-on pilot
  // deviation); the server `requirePolicyManagementEnabled`
  // (+ `requirePolicyPublishWritebackEnabled` on the publish path) + the `policy_*`
  // capability gates stay authoritative (a stale-true client still 503s/403s). The
  // RouteGuard is defence-in-depth (never L6 — EoP). Only this sub-module promotes.
  const policyManagementEnabled = user.featureFlags.policyManagementEnabled ?? false;
  const policiesBasePath = '/hr/policies';
  // PRO-0570 EP5 / ADR panel-admin: the review-panel MEMBER surface authorises by
  // panel membership (ABAC) on the server (`requirePanelMembership`), not a role —
  // a member may hold NO complaint role. So it mounts on the flags alone (no
  // RouteGuard) and is ranked above the role-guarded `/complaints/*` splat by its
  // more-specific static `panel` segment. Admin panel routes stay inside the
  // role-guarded ComplaintsSurface (`/complaints/:id/panel`).
  const complaintPanelEnabled = user.featureFlags.complaintPanelEnabled;
  // PRO-0590 / ADR-038: the Insights module mounts ONLY when the flag is on; the
  // server `requireInsightsEnabled` + `requireCapability` gates stay authoritative
  // (a stale-true client still 503s/403s). The outer guard is the union of every
  // lens role (defence in depth); each lens re-guards inside `InsightsSurface`.
  const insightsEnabled = user.featureFlags.insightsEnabled;
  // PRO-0430 EP1 / ADR-018 REFERRAL_* amendment: the Referrals module (RIP)
  // promotes from its coming-soon nested placeholder to the INERT `ReferralsSurface`
  // scaffold (empty states only — no data path until EP2) ONLY when the flag is on;
  // the server `requireReferralsEnabled` gate stays authoritative once EP2 endpoints
  // exist. The guard is role-keyed (`anyOf={REFERRAL_ROLES}` → `hasAnyRole`), so an
  // unentitled user is redirected Home (defence-in-depth).
  const referralsEnabled = user.featureFlags.referralsEnabled;
  // PRO-0630 / ADR-039: the Comms "Scheduled" management surface mounts ONLY when
  // the flag is on; the server `requireScheduleSendEnabled` + `compose_send` gates
  // stay authoritative (a stale-true client still 503s/403s). The RouteGuard is
  // defence-in-depth (an L1 sender), and the immediate-send path is unchanged.
  const scheduleSendEnabled = user.featureFlags.scheduleSendEnabled;
  // PRO-0740 / ADR-014 amendment: the account-scoped Profile Settings page mounts at
  // `/account` ONLY when the flag is on, and the two old Referrals routes redirect to
  // it. No `RouteGuard` — the page grants nothing by itself (its role-scoped sections
  // render server-authoritative editors); when off, `/account` is unmounted and the
  // Referrals routes behave byte-for-byte as today.
  const profileSettingsEnabled = user.featureFlags.profileSettingsEnabled;
  // PRO-0820 Sub-slice A / ADR-030/028/044: the Sites (CDP) → Zoho projection-health
  // surface mounts ONLY when the flag is on; the server
  // `requireCommercialSitesZohoEnabled` + `requireCapability('commercial_sites_projection_manage')`
  // gates stay authoritative (a stale-true client still 503s/403s). The RouteGuard is
  // defence-in-depth (never L6 — EoP).
  const commercialSitesZohoEnabled = user.featureFlags.commercialSitesZohoEnabled;
  // PRO-0510 / ADR-029 (+A1/A2): the Commercial Data Platform foundation module mounts at
  // `/commercial/*` ONLY when the flag is on; the server `requireCommercialDataEnabled` +
  // per-endpoint `requireCapability` gates stay authoritative (a stale-true client still
  // 503s/403s). The RouteGuard is defence-in-depth (never L6 — EoP). This splat is ranked
  // BELOW the static `/commercial/sites/projection` route (react-router specificity), so
  // the two coexist.
  const commercialDataEnabled = user.featureFlags.commercialDataEnabled;
  // PRO-1260 Epic 3 — placements board is reachable for PRINCIPAL/SBM/PLACEMENT_MANAGER
  // when contracts are on even if the CDP foundation flag is off (those personas do not
  // need customers/catalogue). Mount the splat when either flag is true; per-tab gates
  // inside CommercialRoutes stay authoritative.
  const commercialContractsEnabled = user.featureFlags.commercialContractsEnabled === true;
  const commercialModuleEnabled = commercialDataEnabled || commercialContractsEnabled;
  // PRO-0980 / ADR-048: the "Buy Something via Amazon Business" sub-module of the (still
  // coming-soon) Finance module promotes from its inert placeholder to the functional,
  // capability-gated `PurchasingSurface` ONLY when `purchasingAmazonEnabled` is on; the
  // server `requirePurchasingAmazonEnabled` + `purchase_request` gates stay authoritative
  // (a stale-true client still 503s/403s). The reconciliation queue is a SECOND flag
  // (`purchasingReconcileEnabled`) layered on top, for the AP_RECONCILER. RouteGuards are
  // defence-in-depth (never L6 — EoP). Track A is synthetic/no-egress.
  const purchasingAmazonEnabled = user.featureFlags.purchasingAmazonEnabled ?? false;
  const purchasingReconcileEnabled = user.featureFlags.purchasingReconcileEnabled ?? false;
  // PRO-1350 Epic 6 / ADR-014 A15 — People and Finance workbenches mount only when
  // the module flag AND the domain flag are on. Server 503-before-403 stays
  // authoritative. Oversight roles are not admitted (D37).
  const serviceOpsEnabled = user.featureFlags.serviceOpsEnabled === true;
  const serviceOpsPeoplePath = '/hr/service-operations';
  const serviceOpsFinancePath = '/finance/service-operations';
  const serviceOpsPeopleMounted =
    serviceOpsEnabled && user.featureFlags.serviceOpsPeopleEnabled === true;
  const serviceOpsFinanceMounted =
    serviceOpsEnabled && user.featureFlags.serviceOpsFinanceEnabled === true;
  // PRO-2010 / ADR-080: the HR Cases queue mounts only when `hrCasesEnabled` is on; the
  // server `requireHrCasesEnabled` 503-before-403 + `hr_case_work` gates stay
  // authoritative. The RouteGuard is defence-in-depth (never L6 — EoP). A colleague's own
  // cases live under Ask TAG (`/hr/people-assistant/cases`), not here.
  const hrCasesEnabled = user.featureFlags.hrCasesEnabled === true;
  const hrCasesBasePath = '/hr/cases';
  // PRO-3050 / ADR-081: the attendance module mounts only when `attendanceEnabled` is on; the
  // server `requireAttendanceEnabled` 503-before-403 and per-school capability gates stay
  // authoritative. The RouteGuard is defence in depth (never L6 — no attendance capability).
  const attendanceEnabled = user.featureFlags.attendanceEnabled === true;
  // PRO-0710 / ADR-083: the trips module mounts only when `tripsEnabled` is on; the server
  // `requireTripsEnabled` 503-before-403 and per-school capability gates stay authoritative.
  // The RouteGuard is defence in depth (never L6 — no trip capability).
  const tripsEnabled = user.featureFlags.tripsEnabled === true;
  // PRO-3100 / ADR-084: the Weekly Report module mounts only when `opsIssuesEnabled` is on; the
  // server `requireOpsIssuesEnabled` 503-before-403 and per-school capability gates stay
  // authoritative. The RouteGuard is defence in depth (never L6 — no ops capability).
  const opsIssuesEnabled = user.featureFlags.opsIssuesEnabled === true;
  // PRO-3140 / ADR-087: the Ofsted module mounts only when `ofstedReadinessEnabled` is on (already
  // ANDed with `opsIssuesEnabled` on the server); `requireOfstedReadinessEnabled` 503-before-403
  // and per-provision capability gates stay authoritative. The RouteGuard is defence in depth
  // (never L6 — no Ofsted capability). PRO-3150 TIP-D18: readiness or the inspection workspace
  // mounts it; each task inside keeps its own flag.
  const ofstedEnabled = ofstedModuleEnabled(user.featureFlags);
  // PRO-3210 / ADR-088: Marketing mounts only when `marketingRequestsEnabled` is on.
  const marketingEnabled = user.featureFlags.marketingRequestsEnabled === true;
  // PRO-3220 Phase 3 / ADR-089: Pupil risk mounts only when `pupilRiskEnabled` is on. The server
  // `requirePupilRiskEnabled` 503 and per-school capability checks stay authoritative.
  const pupilRiskEnabled = user.featureFlags.pupilRiskEnabled === true;
  // PRO-3060 / ADR-082: the Surveys area of Comms mounts only when `surveysEnabled` is on; the
  // server `requireSurveysEnabled` 503-before-403 and capability gates stay authoritative.
  // The RouteGuard is defence in depth (never L6 — no survey capability).
  const surveysEnabled = user.featureFlags.surveysEnabled === true;
  return (
    <Suspense fallback={<RouteLoading />}>
      <Routes>
        <Route path="/" element={<HomeView user={user} />} />
        <Route path="/welcome" element={<WelcomeView />} />
        <Route
          path="/compose/*"
          element={
            <RouteGuard
              user={user}
              anyOf={[...COMPOSE_ROLES]}
              label="Compose"
              requestCapability="compose_send"
            >
              <ComposeRoute user={user} />
            </RouteGuard>
          }
        />
        <Route
          path="/approvals"
          element={
            <RouteGuard
              user={user}
              anyOf={[...APPROVALS_ROLES]}
              label="Approvals"
              requestCapability="approve_broadcast"
            >
              <ApprovalSurfaces user={user} />
            </RouteGuard>
          }
        />
        {/* Portal-ops platform tier (PRO-0440 / ADR-014 Amendment 5) — guard relocated, not widened. */}
        <Route
          path="/platform/*"
          element={
            <RouteGuard
              user={user}
              anyOf={[...PLATFORM_ADMIN_ROLES]}
              label="Platform administration"
            >
              <PlatformRoutes user={user} />
            </RouteGuard>
          }
        />
        {/* Comms settings consolidation (PRO-0440 / ADR-014 Amendment 5) — stays inside
            the Comms module; guard relocated from /admin, not widened. */}
        <Route
          path="/settings/*"
          element={
            <RouteGuard user={user} anyOf={[...PLATFORM_ADMIN_ROLES]} label="Comms settings">
              <SettingsRoutes user={user} />
            </RouteGuard>
          }
        />
        {schoolTemplateAuthoringEnabled ? (
          <Route
            path="/settings/school-templates"
            element={
              <RouteGuard
                user={user}
                anyOf={[...SCHOOL_TEMPLATE_ROLES]}
                label="School templates"
              >
                <SchoolTemplatesPage />
              </RouteGuard>
            }
          />
        ) : null}
        {/* PRO-0440 client redirects (guard-preserving — targets are themselves guarded):
            schools workspace -> /platform/schools; dispatch throttle -> /settings/delivery;
            templates -> /settings/templates (splat + query preserved via RedirectSplat). */}
        <Route path="/admin" element={<Navigate to="/platform/schools" replace />} />
        <Route path="/admin/settings" element={<Navigate to="/settings/delivery" replace />} />
        <Route path="/admin/schools/:id" element={<Navigate to="/platform/schools" replace />} />
        <Route path="/admin/schools/new" element={<Navigate to="/platform/schools" replace />} />
        <Route path="/admin/templates/*" element={<RedirectSplat to="/settings/templates" />} />
        {/* Monitoring (`/ops`) — the L6 cross-school delivery/error view. Mounted
            directly (not via SchoolSurfaces' tenant-mode switch) so a platform admin
            who ALSO holds a school-scoped role still sees Monitoring rather than the
            single-school switcher. The view self-fetches GET /ops/delivery-summary,
            which is L6-role-gated server-side; the RouteGuard is defence-in-depth. */}
        <Route
          path="/ops"
          element={
            <RouteGuard user={user} anyOf={[...PLATFORM_ADMIN_ROLES]} label="Monitoring">
              <CrossSchoolOpsView />
            </RouteGuard>
          }
        />
        {scheduleSendEnabled ? (
          <Route
            path="/scheduled"
            element={
            <RouteGuard
              user={user}
              anyOf={[...COMPOSE_ROLES]}
              label="Scheduled sends"
              requestCapability="compose_send"
            >
              <ScheduledSendsView user={user} />
            </RouteGuard>
            }
          />
        ) : null}
        {surveysEnabled ? (
          <Route
            path="/surveys/*"
            element={
              <RouteGuard user={user} anyOf={[...SURVEY_ROUTE_ROLES]} label="Surveys">
                <SurveysRoutes user={user} />
              </RouteGuard>
            }
          />
        ) : null}
        {complaintsEnabled && complaintPanelEnabled ? (
          <Route path="/complaints/panel/*" element={<PanelMemberSurface />} />
        ) : null}
        {complaintsEnabled ? (
          <Route
            path="/complaints/*"
            element={
            <RouteGuard
              user={user}
              anyOf={[...COMPLAINTS_ROUTE_ROLES]}
              label="Complaints"
              requestCapability="complaint_record"
            >
              <ComplaintsSurface user={user} />
            </RouteGuard>
            }
          />
        ) : null}
        {atrEnabled ? (
          <Route
            path={`${atrBasePath}/*`}
            element={
            <RouteGuard
              user={user}
              anyOf={[...atrRouteRoles]}
              label="Request New Hire (ATR)"
              requestCapability="atr_submit"
            >
              <AtrSurface user={user} />
            </RouteGuard>
            }
          />
        ) : null}
        {hrAssistantEnabled ? (
          <Route
            path={`${hrAssistantBasePath}/*`}
            element={
              <HrAssistantRouteGuard user={user}>
                <HrPeopleAssistantSurface user={user} />
              </HrAssistantRouteGuard>
            }
          />
        ) : null}
        {interviewComplianceSurfaceMounted ? (
          <Route
            path={`${hrInterviewComplianceBasePath}/*`}
            element={
            <RouteGuard
              user={user}
              anyOf={[...HR_INTERVIEW_COMPLIANCE_ROLES, ...HR_ER_MEETING_ROLES]}
              label="Interview Compliance"
            >
              <InterviewComplianceSurface user={user} />
            </RouteGuard>
            }
          />
        ) : null}
        {policyManagementEnabled ? (
          <Route
            path={`${policiesBasePath}/*`}
            element={
            <RouteGuard
              user={user}
              anyOf={[...POLICY_ROLES]}
              baselineCapability="policy_read"
              label="Policies"
            >
              <PoliciesSurface user={user} />
            </RouteGuard>
            }
          />
        ) : null}
        {insightsEnabled ? (
          <Route
            path="/insights/*"
            element={
            <RouteGuard user={user} anyOf={[...INSIGHTS_ROUTE_ROLES]} label="Insights">
              <InsightsSurface user={user} />
            </RouteGuard>
            }
          />
        ) : null}
        {attendanceEnabled ? (
          <Route
            path="/attendance/*"
            element={
              <RouteGuard user={user} anyOf={[...ATTENDANCE_ROUTE_ROLES]} label="Attendance">
                <AttendanceSurface user={user} />
              </RouteGuard>
            }
          />
        ) : null}
        {tripsEnabled ? (
          <Route
            path="/trips/*"
            element={
              <RouteGuard
                user={user}
                anyOf={[...tripsRouteRoles(user.featureFlags)]}
                label="Trips"
              >
                <TripsSurface user={user} />
              </RouteGuard>
            }
          />
        ) : null}
        {opsIssuesEnabled ? (
          <Route
            path="/weekly-report/*"
            element={
              <RouteGuard user={user} anyOf={[...OPS_ROUTE_ROLES]} label="Weekly Report">
                <WeeklyReportSurface user={user} />
              </RouteGuard>
            }
          />
        ) : null}
        {ofstedEnabled ? (
          <Route
            path="/ofsted/*"
            element={
              <RouteGuard user={user} anyOf={[...OFSTED_ROUTE_ROLES]} label="Ofsted">
                <OfstedSurface user={user} />
              </RouteGuard>
            }
          />
        ) : null}
        {marketingEnabled ? (
          <Route
            path="/marketing/*"
            element={
              <RouteGuard
                user={user}
                anyOf={[...MARKETING_ROUTE_ROLES]}
                baselineCapability="marketing_request_submit"
                label="Marketing"
              >
                <MarketingSurface user={user} />
              </RouteGuard>
            }
          />
        ) : null}
        {pupilRiskEnabled ? (
          <Route
            path="/pupil-risk/*"
            element={
              <RouteGuard user={user} anyOf={[...PUPIL_RISK_ROUTE_ROLES]} label="Pupil risk">
                <PupilRiskSurface user={user} />
              </RouteGuard>
            }
          />
        ) : null}
        {referralsEnabled ? (
          <Route
            path="/referrals/*"
            element={
            <RouteGuard user={user} anyOf={[...REFERRAL_ROLES]} label="Referrals">
              <ReferralsSurface user={user} />
            </RouteGuard>
            }
          />
        ) : null}
        {myWorkAppliesToMe(user) ? (
          <Route
            path="/my-work"
            element={
              <Suspense fallback={<RouteLoading />}>
                <MyWorkPage />
              </Suspense>
            }
          />
        ) : null}
        {profileSettingsEnabled ? (
          <Route path="/account" element={<ProfileSettingsPage user={user} />} />
        ) : null}
        {commercialSitesZohoEnabled ? (
          <Route
            path="/commercial/sites/projection"
            element={
            <RouteGuard
              user={user}
              anyOf={[...COMMERCIAL_SITES_ROLES]}
              label="Commercial site projection"
            >
              <CommercialSitesProjectionSurface user={user} />
            </RouteGuard>
            }
          />
        ) : null}
        {commercialModuleEnabled ? (
          <Route
            path="/commercial/*"
            element={
            <RouteGuard user={user} anyOf={[...COMMERCIAL_DATA_ROLES]} label="Commercial">
              <CommercialRoutes user={user} />
            </RouteGuard>
            }
          />
        ) : null}
        {purchasingAmazonEnabled ? (
          <Route
            path="/finance/buy-something/*"
            element={
            <RouteGuard user={user} anyOf={[...PURCHASING_BUYER_ROLES]} label="Buy something">
              <PurchasingSurface user={user} />
            </RouteGuard>
            }
          />
        ) : null}
        {serviceOpsPeopleMounted ? (
          <Route
            path={`${serviceOpsPeoplePath}/*`}
            element={
            <RouteGuard
              user={user}
              anyOf={[...SERVICE_OPS_PEOPLE_ROLES]}
              label="Service Operations"
            >
              <ServiceOpsSurface user={user} domain="people" basePath={serviceOpsPeoplePath} />
            </RouteGuard>
            }
          />
        ) : null}
        {serviceOpsFinanceMounted ? (
          <Route
            path={`${serviceOpsFinancePath}/*`}
            element={
            <RouteGuard
              user={user}
              anyOf={[...SERVICE_OPS_FINANCE_ROLES]}
              label="Service Operations"
            >
              <ServiceOpsSurface user={user} domain="finance" basePath={serviceOpsFinancePath} />
            </RouteGuard>
            }
          />
        ) : null}
        {hrCasesEnabled ? (
          <Route
            path={`${hrCasesBasePath}/*`}
            element={
              <RouteGuard user={user} anyOf={[...HR_CASE_ROLES]} label="HR Cases">
                <HrCasesSurface />
              </RouteGuard>
            }
          />
        ) : null}
        {purchasingAmazonEnabled && purchasingReconcileEnabled ? (
          <Route
            path="/finance/reconciliation/*"
            element={
            <RouteGuard user={user} anyOf={[...PURCHASING_RECONCILE_ROLES]} label="Reconciliation">
              <ReconciliationSurface />
            </RouteGuard>
            }
          />
        ) : null}
        {/* PRO-0740 / ADR-014 amendment (SF-1/SF-3): the two re-homed Referrals surfaces
            redirect to `/account` when the flag is on. These static two-segment paths
            out-rank the `/referrals/*` splat above (react-router v6 ranks by specificity,
            not declaration order — like `/admin/schools/:id` above its neighbours), so the
            redirect wins over `ReferralsSurface`. When off, they are not registered and
            `ReferralsSurface` keeps today's behaviour byte-for-byte. */}
        {profileSettingsEnabled ? (
          <Route path="/referrals/my-coverage" element={<Navigate to="/account" replace />} />
        ) : null}
        {profileSettingsEnabled ? (
          <Route path="/referrals/service-profiles" element={<Navigate to="/account" replace />} />
        ) : null}
        {modulesConfig
          .filter(
            (module) =>
              module.status === 'coming-soon' &&
              module.navShape === 'nested' &&
              !(complaintsEnabled && module.id === 'complaints') &&
              !(referralsEnabled && module.id === 'referrals'),
          )
          .map((module) => (
            <Route
              key={module.basePath}
              path={module.basePath}
              element={
                <ModulePlaceholder
                  title={module.label}
                  comingSoon
                  description={module.placeholderSummary ?? module.summary}
                  planningNote={PLACEHOLDER_PLANNING_DISCLAIMER}
                  roadmapHref={placeholderWikiHref(module.wikiPath)}
                />
              }
            />
          ))}
        {nestedPlaceholderPaths()
          .filter((entry) => !(complaintsEnabled && entry.path.startsWith('/complaints')))
          .filter((entry) => !(atrEnabled && entry.path.startsWith(atrBasePath)))
          .filter((entry) => !(referralsEnabled && entry.path.startsWith('/referrals')))
          .filter(
            (entry) => !(purchasingAmazonEnabled && entry.path.startsWith('/finance/buy-something')),
          )
          .filter((entry) => !(hrAssistantEnabled && entry.path.startsWith(hrAssistantBasePath)))
          .filter(
            (entry) =>
              !(
                interviewComplianceSurfaceMounted &&
                entry.path.startsWith(hrInterviewComplianceBasePath)
              ),
          )
          .filter((entry) => !(policyManagementEnabled && entry.path.startsWith(policiesBasePath)))
          .filter(
            (entry) => !(serviceOpsPeopleMounted && entry.path.startsWith(serviceOpsPeoplePath)),
          )
          .filter(
            (entry) => !(serviceOpsFinanceMounted && entry.path.startsWith(serviceOpsFinancePath)),
          )
          .filter((entry) => !(hrCasesEnabled && entry.path.startsWith(hrCasesBasePath)))
          .map((entry) => (
            <Route
              key={entry.path}
              path={entry.path}
              element={
                <ModulePlaceholder
                  title={entry.title}
                  comingSoon
                  description={entry.placeholderSummary}
                  planningNote={entry.planningNote}
                  roadmapHref={placeholderWikiHref(entry.wikiPath)}
                />
              }
            />
          ))}
        <Route path="*" element={<NotFoundView />} />
      </Routes>
    </Suspense>
  );
}
